import { MARKET_LOCALE, type Market } from './types';

/**
 * ARCHITECTURE §10.8。
 *
 * compact_style 是 meta.metric_registry 的欄位，不是 UI 裡的 if：
 * 「融資餘額 29.2千」與「成交值 1,234億」都正確，而 UI 裡沒有任何一行
 * `if (metricKey === ...)`。P0 先把格式化器放好，registry 在 P1 接上。
 */
export type CompactStyle = 'tw_money' | 'tw_count' | 'si' | 'plain';

export function formatTW(value: number, style: CompactStyle, prec = 2): string {
  switch (style) {
    case 'tw_money': // 金額：萬 / 億
      if (Math.abs(value) >= 1e8) return `${(value / 1e8).toFixed(prec)}億`;
      if (Math.abs(value) >= 1e4) return `${(value / 1e4).toFixed(prec)}萬`;
      return value.toFixed(prec);
    // 張／股。**千的上界是 10 萬，不是 1 萬。**
    //
    // 這一行原本先測 >= 1e4，於是 29,282 張變成「2.93萬」，而 `千` 這個單位在
    // 10,000 以上永遠到不了 —— 與 ROADMAP §5 的 Demo 逐字牴觸
    // （「融資餘額 29.2千、區間 13.1千–34.5千」），也與它自己上一行的註解牴觸。
    //
    // 為什麼跟規格而不是跟語感：單看一個數字，「2.9萬張」確實比「29.2千張」
    // 順口。但 KPI 卡上那個數字**不是單獨出現的**，它右邊就是 1Y 區間
    // 13.1千–34.5千，而 range bar 的三個數字必須是同一個單位，否則使用者要在
    // 「1.3萬 / 2.9萬 / 3.5萬」之間自己換算才看得出現在的水位。
    // 參考網站與 ROADMAP 的 Demo 選 `千` 就是為了讓這三個數字同尺度。
    // 上界取 1e5：到了 10 萬張以上，`千` 會變成三位數（120.0千），那時 `萬` 才對。
    case 'tw_count':
      if (Math.abs(value) >= 1e5) return `${(value / 1e4).toFixed(prec)}萬`;
      if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(prec)}千`;
      return String(value);
    case 'si':
      return new Intl.NumberFormat('en', { notation: 'compact' }).format(value);
    default:
      return new Intl.NumberFormat('zh-TW', {
        minimumFractionDigits: prec,
        maximumFractionDigits: prec,
      }).format(value);
  }
}

/** 百分比。帶正負號，因為它幾乎總是用在漲跌語境。 */
export function formatPercent(value: number, prec = 2, signed = true): string {
  const body = `${Math.abs(value).toFixed(prec)}%`;
  if (!signed) return body;
  if (value > 0) return `+${body}`;
  if (value < 0) return `−${body}`; // U+2212 minus，與 tabular-nums 的 digit 同寬
  return body;
}

/** 序數百分位：「第 91 百分位」。§15.2 偏好排名框架而非機率框架。 */
export function formatPercentileZh(percentile: number): string {
  return `第 ${Math.round(percentile)} 百分位`;
}

export function formatMoney(value: number, market: Market, prec = 2): string {
  const { locale, currency } = MARKET_LOCALE[market];
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: prec,
    maximumFractionDigits: prec,
  }).format(value);
}

/**
 * 漲跌方向 → CSS class。元件永遠拿 'text-up' / 'text-down'，
 * 真正的顏色由 documentElement 上的 [data-market] 決定（§10.6）。
 * 這個函式裡沒有任何一個市場的判斷——有的話就是把互換寫死了。
 */
export function directionClass(delta: number): 'text-up' | 'text-down' | 'text-flat' {
  if (delta > 0) return 'text-up';
  if (delta < 0) return 'text-down';
  return 'text-flat';
}
